"use client";

import { useEffect, useState } from "react";
import { DetailValueTypeEnumValue } from "@panda0/panda0-graphql/defs";
import { Button } from "@panda0/panda0-ui";
import { AlignLeft, CheckSquare, CircleCheck, TypeIcon } from "lucide-react";

interface InputTypeButtonsProps {
  defaultValue?: DetailValueTypeEnumValue;
  onChange?: (value: DetailValueTypeEnumValue) => void;
}

export default function InputTypeButtons({
  defaultValue = DetailValueTypeEnumValue.oneline,
  onChange,
}: InputTypeButtonsProps) {
  const [selected, setSelected] =
    useState<DetailValueTypeEnumValue>(defaultValue);

  // Update internal state when defaultValue changes
  useEffect(() => {
    if (defaultValue !== selected) {
      setSelected(defaultValue);
    }
  }, [defaultValue]);

  const handleSelect = (type: DetailValueTypeEnumValue) => {
    setSelected(type);
    onChange?.(type);
  };

  return (
    <div className="flex justify-center items-center gap-2 w-full">
      <Button
        variant={
          selected === DetailValueTypeEnumValue.oneline
            ? "primary"
            : "secondary"
        }
        size="icon"
        onClick={() => handleSelect(DetailValueTypeEnumValue.oneline)}
        aria-label="Single line text input"
        title="Single line text input"
        icon={<TypeIcon className="h-4 w-4" />}
      ></Button>

      <Button
        variant={
          selected === DetailValueTypeEnumValue.multiline
            ? "primary"
            : "secondary"
        }
        size="icon"
        onClick={() => handleSelect(DetailValueTypeEnumValue.multiline)}
        aria-label="Multi-line text input"
        title="Multi-line text input"
        icon={<AlignLeft className="h-4 w-4" />}
      ></Button>

      <Button
        variant={
          selected === DetailValueTypeEnumValue.radiobutton
            ? "primary"
            : "secondary"
        }
        size="icon"
        onClick={() => handleSelect(DetailValueTypeEnumValue.radiobutton)}
        aria-label="Select one option"
        title="Select one option"
        icon={<CircleCheck className="h-4 w-4" />}
      ></Button>

      <Button
        variant={
          selected === DetailValueTypeEnumValue.checkbox
            ? "primary"
            : "secondary"
        }
        size="icon"
        onClick={() => handleSelect(DetailValueTypeEnumValue.checkbox)}
        aria-label="Checkbox option"
        title="Checkbox option"
        icon={<CheckSquare className="h-4 w-4" />}
      ></Button>
    </div>
  );
}
